/* Modal M1 — Resultado del contacto / T3 agendar cita (unificado). */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ModalCita from "./ModalCita";
import FichaResumenPropiedad from "./propiedades/FichaResumenPropiedad";
import { dialogs } from "../context/DialogContext";
import { useDraggableModalPanel } from "../hooks/useDraggableModalPanel";
import usePermiso from "../hooks/usePermiso";
import {
  T3_VISIT_CONFIRM_OUTCOMES,
  QUALIFICATION_DEFER_LABEL,
  getQualificationOriginLabel,
  getM1ModalMode,
  getM1QualificationOutcomes,
  isT3bVisitConfirmWait,
} from "../constants/leadFlowOutcomes";
import { TASK_UI } from "../constants/taskUiColors";
import { formatApiError } from "../utils/helpers";
import {
  addMinutosHora,
  buildCitaDateTimeISO,
  ensureVisitaRangoISO,
} from "../utils/citaAgenda";

function inicialesContacto(nombre) {
  const trozos = String(nombre || "").trim().split(/\s+/).filter(Boolean);
  return (trozos.map((x) => x[0]).join("").slice(0, 2) || "CL").toUpperCase();
}

function parseExtras(lead) {
  const dx = lead?.datos_extras;
  if (!dx) return {};
  if (typeof dx === "object") return dx;
  try {
    return JSON.parse(dx);
  } catch {
    return {};
  }
}

/** Permiso granular (t_m1_*) para cada opción del modal M1, según su código y modo. */
function m1OpcionNodeId(op, esT3) {
  switch (op.codigo) {
    case "cita_creada":
      return esT3 ? "t_m1_concertar_cita" : "t_m1_cita_creada";
    case "enviar_propiedad":
    case "enviar_whatsapp":
      return "t_m1_whatsapp";
    case "volver_a_llamar":
      return "t_m1_volver_llamar";
    case "frios":
      return "t_m1_frios";
    case "eliminados":
      return "t_m1_eliminados";
    default:
      return null;
  }
}

function colorTextoOpcion(op) {
  if (op.codigo === "cita_creada") return "#ffffff";
  if (op.codigo === "volver_a_llamar") return "#000000";
  return op.textColor || "#000000";
}

function mapPropiedadParaCita(p) {
  if (!p) return null;
  let propietarios = [];
  if (Array.isArray(p.propietarios)) propietarios = p.propietarios;
  else if (typeof p.propietarios === "string") {
    try {
      propietarios = JSON.parse(p.propietarios);
    } catch {
      propietarios = [];
    }
  }
  return {
    id: p.id,
    titulo: p.titulo || p.tipo_nombre || "Sin título",
    detalle: `${p.tipo_nombre || p.tipo || ""} · ${p.poblacion_nombre || p.poblacion || ""}`.trim(),
    precio: p.precio ? `${Number(p.precio).toLocaleString("es-ES")} €` : "Consultar",
    referencia: p.referencia,
    tenemos_llaves: p.tenemos_llaves || false,
    num_llave: p.num_llave || null,
    recoger_llaves_en: p.recoger_llaves_en || null,
    propietarios,
    info_visita: p.info_visita != null ? String(p.info_visita) : "",
  };
}

async function fetchPropiedadCompletaParaCita(p) {
  if (!p?.id) return mapPropiedadParaCita(p);
  try {
    const [detRes, propRes] = await Promise.all([
      api.get(`/propiedades/${p.id}`),
      api.get(`/propiedades/${p.id}/propietarios`).catch(() => ({ data: { data: [] } })),
    ]);
    const det = detRes.data?.data || detRes.data?.propiedad || detRes.data || p;
    const propietarios = propRes.data?.data || propRes.data || [];
    return mapPropiedadParaCita({ ...det, propietarios });
  } catch {
    return mapPropiedadParaCita(p);
  }
}

async function aplicarCualificacion(leadId, body) {
  const res = await api.post(`/tareas/lead/${leadId}/cualificacion`, body);
  return res.data;
}

async function aplicarConfirmacion(leadId, body) {
  const res = await api.post(`/tareas/lead/${leadId}/confirmacion-visita`, body);
  return res.data;
}

export default function ModalResultadoCualificacion({
  isOpen,
  onClose,
  lead,
  tarea,
  theme,
  onCompleted,
  onLlamarTelefono,
}) {
  const c = theme?.colors || {};
  const isDark = theme?.name === "dark";
  const navigate = useNavigate();
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const { puede } = usePermiso();
  const [guardando, setGuardando] = useState(false);
  const [modalCitaOpen, setModalCitaOpen] = useState(false);
  const [propiedadesCita, setPropiedadesCita] = useState([]);
  const [cargandoProps, setCargandoProps] = useState(false);
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);
  const [leadActualizado, setLeadActualizado] = useState(lead);
  const [tareaIdCita, setTareaIdCita] = useState(null);
  const [citaPendiente, setCitaPendiente] = useState(null);

  const esClienteActivo = parseExtras(leadActualizado)?.relacion_cliente === "cliente_activo";

  const nombreContacto = [leadActualizado?.nombre, leadActualizado?.apellido].filter(Boolean).join(" ").trim() || "Cliente";
  const modo = getM1ModalMode(tarea);
  const esT3 = modo === "t3";
  const subtitulo = esT3
    ? "El cliente respondió por WhatsApp. Indica qué hacer a continuación; debajo de cada botón verás la tarea que se creará."
    : getQualificationOriginLabel(tarea);
  const opcionesBase = esT3
    ? T3_VISIT_CONFIRM_OUTCOMES
    : getM1QualificationOutcomes(leadActualizado, tarea);
  // Cada botón de resultado es un nodo t_m1_* independiente: se oculta por completo si no
  // tiene "ver" (por defecto todos los nodos son permisivos, así que no cambia nada).
  const opciones = opcionesBase.filter((op) => puede(m1OpcionNodeId(op, esT3), "ver"));
  const puedeAhoraNo = puede("t_m1_ahora_no", "ver");

  useEffect(() => {
    setLeadActualizado(lead);
  }, [lead]);

  const cargarPropiedadesParaCita = useCallback(async (propiedadRefs) => {
    setCargandoProps(true);
    try {
      const refsRaw = Array.isArray(propiedadRefs) ? propiedadRefs : [propiedadRefs];
      const refs = refsRaw.map((r) => String(r || "").trim()).filter(Boolean);
      const lista = [];
      for (const ref of refs) {
        try {
          const r = await api.get(`/propiedades/validar-ref/${encodeURIComponent(ref)}`);
          const p = r.data?.propiedad || r.data?.data;
          if (p?.id && !lista.some((x) => String(x.id) === String(p.id))) {
            const mapped = await fetchPropiedadCompletaParaCita(p);
            if (mapped) lista.push(mapped);
          }
        } catch {
          /* sin ref válida */
        }
      }
      // Si no hay referencias (ni último envío ni origen), dejamos la lista vacía:
      // el agente añade por referencia la(s) propiedad(es) acordada(s) con el cliente.
      setPropiedadesCita(lista);
    } catch {
      setPropiedadesCita([]);
    } finally {
      setCargandoProps(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setModalCitaOpen(false);
      setTareaIdCita(null);
      setCitaPendiente(null);
      return;
    }
    // Cliente existente: comprobar si ya tiene una cita de visita pendiente
    // (para ofrecer "asignar propiedad a la cita existente").
    if (esClienteActivo && !esT3 && leadActualizado?.cliente_id) {
      let cancel = false;
      (async () => {
        try {
          const r = await api.get(`/tareas/cliente/${leadActualizado.cliente_id}/cita-pendiente`);
          if (!cancel) setCitaPendiente(r.data?.tiene_cita ? r.data.cita || true : null);
        } catch {
          if (!cancel) setCitaPendiente(null);
        }
      })();
      return () => {
        cancel = true;
      };
    }
  }, [isOpen, esClienteActivo, esT3, leadActualizado?.cliente_id]);

  const ejecutarAccionCliente = async (accion, confirmMsg) => {
    if (confirmMsg) {
      const ok = await dialogs.confirm(confirmMsg);
      if (!ok) return;
    }
    const data = await ejecutarAccion(accion);
    if (data?.ok !== false) onClose?.();
  };

  const ejecutarAccion = async (accion, extra = {}) => {
    if (!lead?.id || !tarea?.id || guardando) return null;
    setGuardando(true);
    try {
      const apiFn = esT3 ? aplicarConfirmacion : aplicarCualificacion;
      const data = await apiFn(lead.id, {
        accion,
        tarea_id: tarea.id,
        ...extra,
      });
      if (data?.ok === false) {
        await dialogs.alert(formatApiError(data, "No se pudo guardar el resultado"));
        return null;
      }
      if (data?.lead) setLeadActualizado(data.lead);
      await onCompleted?.(data);
      return data;
    } catch (e) {
      await dialogs.alert(formatApiError(e, "No se pudo guardar el resultado"));
      return null;
    } finally {
      setGuardando(false);
    }
  };

  const handleCerrar = async () => {
    if (guardando) return;
    const ok = await dialogs.confirm(
      "¿Cerrar sin guardar? El texto o la selección que hayas hecho se perderá."
    );
    if (!ok) return;
    onClose?.();
  };

  const handleAhoraNo = async () => {
    const enT3b = esT3 && isT3bVisitConfirmWait(tarea);
    const ok = await dialogs.confirm(
      enT3b
        ? "Se cerrará este modal y la tarea seguirá en espera de respuesta. Podrás completar el resultado cuando quieras."
        : esT3
          ? `Tienes ${QUALIFICATION_DEFER_LABEL} para indicar el resultado. Si no lo haces, el lead pasará a Fríos.`
          : `Tienes ${QUALIFICATION_DEFER_LABEL} para indicar el resultado del contacto. Si no lo haces, el lead pasará a Eliminados.\n\nLa tarea seguirá visible en el panel; solo se cierra este modal.`
    );
    if (!ok) return;
    const data = await ejecutarAccion("ahora_no");
    if (data?.ok !== false) {
      await onCompleted?.(data);
      onClose?.();
    }
  };

  const elegirOpcion = async (op) => {
    if (op.codigo === "cita_creada") {
      setGuardando(true);
      try {
        const prepFn = esT3 ? aplicarConfirmacion : aplicarCualificacion;
        const prep = await prepFn(lead.id, {
          accion: "preparar_cita",
          tarea_id: tarea.id,
        });
        if (prep?.ok === false) {
          await dialogs.alert(formatApiError(prep, "No se pudo preparar la cita"));
          return;
        }
        if (prep?.tarea_id) setTareaIdCita(prep.tarea_id);
        if (prep?.lead) setLeadActualizado(prep.lead);
        // Por defecto, la cita se pre-rellena con la(s) propiedad(es) del ÚLTIMO
        // envío (WhatsApp/mail); si no hubo envío, caemos a la propiedad de origen.
        const ultimoEnvio = Array.isArray(prep?.propiedades_ultimo_envio)
          ? prep.propiedades_ultimo_envio.map((p) => p?.referencia).filter(Boolean)
          : [];
        const refsCita = ultimoEnvio.length
          ? ultimoEnvio
          : [prep?.propiedad?.referencia || prep?.lead?.propiedad_ref].filter(Boolean);
        await cargarPropiedadesParaCita(refsCita);
        setModalCitaOpen(true);
      } catch (e) {
        await dialogs.alert(formatApiError(e, "No se pudo preparar la cita"));
      } finally {
        setGuardando(false);
      }
      return;
    }
    if (op.codigo === "frios" || op.codigo === "eliminados") {
      const destino = op.codigo === "frios" ? "Fríos" : "Eliminados";
      const ok = await dialogs.confirm(
        esT3
          ? `¿Confirmas mover el lead y el cliente a ${destino}?`
          : `¿Confirmas mover el lead a ${destino}?`
      );
      if (!ok) return;
      const data = await ejecutarAccion(op.codigo);
      if (data?.ok !== false) onClose?.();
      return;
    }
    if (op.codigo === "enviar_propiedad" || op.codigo === "enviar_whatsapp") {
      const data = await ejecutarAccion(op.codigo === "enviar_whatsapp" ? "enviar_whatsapp" : "enviar_propiedad");
      if (data?.ok !== false) onClose?.();
      return;
    }
    if (op.codigo === "volver_a_llamar") {
      const data = await ejecutarAccion("volver_a_llamar");
      if (data?.ok !== false) onClose?.();
      return;
    }
  };

  const handleCitaConfirm = async (data) => {
    try {
      if (data?.cierreMode === "ana") {
        throw new Error("Ana (IA) aún no está activa. Elige «Yo mismo» para programar la visita.");
      }
      const propsVisita = data?.propiedades || [];

      const agenteId = leadActualizado?.agente_id || tarea?.agente_id || null;
      if (!agenteId) throw new Error("Asigna un agente al lead antes de crear la cita.");

      const fechaInicioRaw = buildCitaDateTimeISO(data.fecha, data.horaInicio);
      const bloques = Math.max(1, propsVisita.length);
      const durMin = data.duracion || 45;
      const horaFin = data.horaFin || addMinutosHora(data.horaInicio, bloques * durMin);
      const fechaFinRaw = buildCitaDateTimeISO(data.fecha, horaFin);
      const { fecha_inicio: fechaInicio, fecha_fin: fechaFin } = ensureVisitaRangoISO(
        fechaInicioRaw,
        fechaFinRaw,
        { bloques, duracionMin: durMin },
      );
      if (!fechaInicio || !fechaFin) throw new Error("Fecha u hora de la visita no válidas.");

      const propiedadIds = propsVisita.map((p) => p.id).filter(Boolean);
      const lineasHorario = propsVisita.map((p) => {
        const ref = p.referencia || p.id;
        return `${p.horaVisita || data.horaInicio} · Ref. ${ref}`;
      });

      setGuardando(true);
      const apiFn = esT3 ? aplicarConfirmacion : aplicarCualificacion;
      const taskIdEfectivo = tareaIdCita || tarea.id;
      const result = await apiFn(lead.id, {
        accion: "cita_creada",
        tarea_id: taskIdEfectivo,
        agenda: {
          agente_id: String(agenteId),
          lead_id: lead.id,
          cliente_id: leadActualizado?.cliente_id || null,
          propiedad_ids: propiedadIds.length ? propiedadIds : null,
          titulo: `Visita — ${nombreContacto}`,
          descripcion: propsVisita.length
            ? (esT3
              ? `Visita programada tras confirmación por WhatsApp.\n${lineasHorario.join("\n")}`
              : `Visita programada tras cualificación del lead.\n${lineasHorario.join("\n")}`)
            : (esT3
              ? "Visita programada tras confirmación por WhatsApp."
              : "Visita programada tras cualificación del lead."),
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      if (result?.ok === false) {
        await dialogs.alert(formatApiError(result, "No se pudo crear la cita"));
        return;
      }
      if (!result?.nextTask?.id && !result?.tarea?.id) {
        await dialogs.alert(
          "La cita se guardó, pero no apareció la tarea T4 · Cita. Recarga Tareas o contacta con soporte."
        );
        return;
      }
      if (result?.lead) setLeadActualizado(result.lead);
      setModalCitaOpen(false);
      onClose?.();
      await onCompleted?.(result);
    } catch (e) {
      await dialogs.alert(formatApiError(e, "No se pudo crear la cita"));
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen || !tarea) return null;

  const overlay = {
    position: "fixed",
    inset: 0,
    zIndex: 1200,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  };

  const panel = {
    background: c.bgPanel || "#fff",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 480,
    border: `1px solid ${c.border || "#e2e8f0"}`,
    boxShadow: isDark ? "0 20px 50px rgba(0,0,0,0.5)" : "0 20px 40px rgba(15,23,42,0.15)",
  };

  return (
    <>
      {!modalCitaOpen && (
      <div style={overlay}>
        <div
          ref={dragRef}
          onClick={(e) => e.stopPropagation()}
          style={{ ...panel, ...panelPositionStyle }}
        >
          <div
            onMouseDown={startDrag}
            style={{
              marginBottom: 16,
              position: "relative",
              paddingRight: 36,
              cursor: "grab",
              userSelect: "none",
            }}
          >
            <button
              type="button"
              onClick={handleCerrar}
              disabled={guardando}
              aria-label="Cerrar"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: c.bgInput,
                cursor: guardando ? "not-allowed" : "pointer",
                fontSize: 16,
                color: c.textMuted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textMuted, marginBottom: 6 }}>
              {esT3 ? "Concertar cita" : "Resultado del contacto"}
            </div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: c.textPrimary }}>{nombreContacto}</h2>
            <p style={{ margin: "8px 0 0", fontSize: 13, color: c.textSecondary, lineHeight: 1.45 }}>{subtitulo}</p>
            {!esT3 && (
              <p style={{ margin: "10px 0 0", fontSize: 12, color: c.textMuted, lineHeight: 1.4 }}>
                Indica qué ha pasado en la conversación para avanzar a la siguiente tarea del embudo.
              </p>
            )}
          </div>

          {esClienteActivo && !esT3 && (
            <div
              style={{
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 10,
                border: "2px solid #dc2626",
                background: isDark ? "rgba(220,38,38,0.16)" : "#fee2e2",
                color: isDark ? "#fecaca" : "#991b1b",
                fontSize: 12.5,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              CLIENTE EXISTENTE. Revisa su ficha antes de decidir. Puedes seguir el flujo
              normal de cita o cerrar este lead manteniendo al cliente.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {opciones.filter((op) => !op.secondary).map((op) => {
              const textColor = colorTextoOpcion(op);
              const accent = op.accent || TASK_UI.espera;
              return (
              <button
                key={op.codigo}
                type="button"
                disabled={guardando || (op.codigo === "cita_creada" && cargandoProps)}
                onClick={() => elegirOpcion(op)}
                style={{
                  textAlign: "left",
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: `2px solid ${accent}`,
                  background: accent,
                  color: textColor,
                  cursor: guardando ? "wait" : "pointer",
                  fontFamily: "inherit",
                  opacity: guardando ? 0.7 : 1,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 800, color: textColor }}>{op.label}</div>
                {op.description && (
                  <div style={{ fontSize: 12, color: textColor, marginTop: 4, lineHeight: 1.4 }}>{op.description}</div>
                )}
              </button>
            );
            })}
          </div>

          {esClienteActivo && !esT3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
              {citaPendiente && (
                <button
                  type="button"
                  disabled={guardando}
                  onClick={() =>
                    ejecutarAccionCliente(
                      "asignar_propiedad_cita_existente",
                      "Se añadirá la propiedad de este lead a la cita pendiente del cliente y el lead se cerrará. ¿Continuar?"
                    )
                  }
                  style={{
                    textAlign: "left",
                    padding: "14px 16px",
                    borderRadius: 10,
                    border: "2px solid #2563eb",
                    background: "#2563eb",
                    color: "#ffffff",
                    cursor: guardando ? "wait" : "pointer",
                    fontFamily: "inherit",
                    opacity: guardando ? 0.7 : 1,
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 800 }}>Asignar propiedad a cita existente</div>
                  <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
                    El cliente ya tiene una cita pendiente: añade esta propiedad y cierra el lead.
                  </div>
                </button>
              )}
              <button
                type="button"
                disabled={guardando}
                onClick={() =>
                  ejecutarAccionCliente(
                    "eliminar_lead_seguir_cliente",
                    "El lead pasará a Eliminados y el cliente continuará en su punto actual. La demanda conserva el interés. ¿Continuar?"
                  )
                }
                style={{
                  textAlign: "left",
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: "2px solid #b91c1c",
                  background: isDark ? "rgba(185,28,28,0.18)" : "#fff1f2",
                  color: isDark ? "#fecaca" : "#991b1b",
                  cursor: guardando ? "wait" : "pointer",
                  fontFamily: "inherit",
                  opacity: guardando ? 0.7 : 1,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 800 }}>Eliminar lead y seguir con el cliente</div>
                <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
                  No procede una nueva cita: cierra este lead manteniendo activo al cliente.
                </div>
              </button>
            </div>
          )}

          <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
            {opciones.filter((op) => op.secondary).map((op) => (
              <button
                key={op.codigo}
                type="button"
                disabled={guardando}
                onClick={() => elegirOpcion(op)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `2px solid ${op.accent}`,
                  background: op.accent,
                  color: op.textColor || "#000000",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {op.label}
              </button>
            ))}
            {puedeAhoraNo && (
              <button
                type="button"
                disabled={guardando}
                onClick={handleAhoraNo}
                style={{
                  marginLeft: "auto",
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${c.border}`,
                  background: c.bgInput,
                  color: c.textSecondary,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Ahora no
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {modalCitaOpen && (
        <ModalCita
          isOpen={modalCitaOpen}
          zIndex={1300}
          onClose={() => setModalCitaOpen(false)}
          onConfirm={handleCitaConfirm}
          contact={{
            nombre: nombreContacto,
            telefono: String(leadActualizado?.telefono || leadActualizado?.whatsapp || "").trim(),
            email: String(leadActualizado?.email || "").trim(),
            iniciales: inicialesContacto(nombreContacto),
            clienteId: leadActualizado?.cliente_id || tarea?.cliente_id || null,
          }}
          onAbrirFichaCliente={(clienteId) => { if (clienteId) navigate(`/clientes/${clienteId}`); }}
          onAbrirFichaContacto={(contactoId) => { if (contactoId) navigate(`/contactos/${contactoId}`); }}
          onAbrirVistaPrevia={setVistaPreviaPropId}
          properties={propiedadesCita}
          agentName={leadActualizado?.agente_nombre || ""}
          onLlamarTelefono={onLlamarTelefono}
          onBuscarPropiedadPorRef={async (ref) => {
            const r = await api.get(`/propiedades/validar-ref/${encodeURIComponent(String(ref).trim())}`);
            const p = r.data?.propiedad || r.data?.data;
            if (!p?.id) return null;
            return await fetchPropiedadCompletaParaCita(p);
          }}
        />
      )}
      {vistaPreviaPropId && (
        <FichaResumenPropiedad propiedadId={vistaPreviaPropId} onClose={() => setVistaPreviaPropId(null)} />
      )}
    </>
  );
}
